import React from 'react';
import { useInView, EASE, DUR } from '../utils/motion';

/**
 * Scroll reveal — the shared primitive this project was missing.
 *
 * Ported from labeau_landing's `Reveal.js`, with its `react-intersection-observer`
 * dependency swapped for the native observer in utils/motion.js. Same idea: one
 * observer per element, then a CSS transition on `opacity` and `transform` only,
 * so the browser can run it on the compositor without layout or paint work.
 *
 * Eight components here each hand-roll their own reveal with a local
 * `useInView` plus a `@keyframes` block; those five-variants-of-the-same-thing
 * are why this exists. New sections should use this instead.
 *
 * Staggering is just arithmetic on `delay`:
 *
 *   {items.map((item, i) => (
 *     <Reveal key={item.id} delay={i * 0.08}>…</Reveal>
 *   ))}
 *
 * Props mirror the framer-motion shape they replace, so the call sites read the
 * same as `<motion.div initial={{opacity:0,y:24}} whileInView={…}>`.
 */
const Reveal = React.forwardRef(function Reveal(
    {
        children,
        as: Tag = 'div',
        y = 24,
        x = 0,
        scale = 1,
        duration = DUR.entrance,
        delay = 0,
        once = true,
        amount = 0.15,
        className,
        style,
        ...rest
    },
    forwardedRef,
) {
    const [ref, inView] = useInView(amount, once);

    // Merge the observer's ref with any ref the caller passed through.
    const setRefs = (node) => {
        ref.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
    };

    const hidden = `translate3d(${x}px, ${y}px, 0)${scale !== 1 ? ` scale(${scale})` : ''}`;

    return (
        <Tag
            ref={setRefs}
            className={className}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'none' : hidden,
                transition: `opacity ${duration}s ease-out, transform ${duration}s ${EASE}`,
                transitionDelay: `${delay}s`,
                // Dropped once the element has arrived: a permanent will-change
                // keeps a compositor layer alive for every revealed node.
                willChange: inView ? 'auto' : 'opacity, transform',
                ...style,
            }}
            {...rest}
        >
            {children}
        </Tag>
    );
});

export default Reveal;
